import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Alert
} from '@mui/material';
import {
    People as PeopleIcon,
    Quiz as QuizIcon,
    TrendingUp as TrendingUpIcon,
    PersonAdd as PersonAddIcon
} from '@mui/icons-material';

const DashboardOverview = ({ onError, onLoading }) => {
    const [overviewData, setOverviewData] = useState({
        totalUsers: 0,
        totalQuizSessions: 0,
        totalScore: 0,
        recentRegistrations: 0
    });
    const [error, setError] = useState(null);

    const fetchOverviewData = useCallback(async () => {
        try {
            setError(null);

            // Fetch user registration data
            const userResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/user-auth/registration-analytics`);
            const userData = await userResponse.json();

            // Fetch quiz analytics data
            const quizResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/user-tracking/comprehensive-analytics`);
            const quizData = await quizResponse.json();

            if (userData.success && quizData.success) {
                // Calculate total users
                const totalUsers = userData.analytics.totalCounts?.reduce((sum, item) => sum + item.count, 0) || 0;

                // Calculate total quiz sessions
                const totalQuizSessions = Object.values(quizData.analytics || {}).reduce((sum, sessions) => {
                    return sum + (Array.isArray(sessions) ? sessions.length : 0);
                }, 0);

                // Calculate total score
                const totalScore = Object.values(quizData.analytics || {}).reduce((sum, sessions) => {
                    if (Array.isArray(sessions)) {
                        return sum + sessions.reduce((sessionSum, session) => sessionSum + (session.total_score || 0), 0);
                    }
                    return sum;
                }, 0);

                // Get recent registrations (last 7 days)
                const recentRegistrations = userData.analytics.recentRegistrations?.filter(user => {
                    const userDate = new Date(user.created_at);
                    const weekAgo = new Date();
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    return userDate >= weekAgo;
                }).length || 0;

                setOverviewData({
                    totalUsers,
                    totalQuizSessions,
                    totalScore,
                    recentRegistrations
                });
            } else {
                const errorMsg = 'Failed to fetch overview data';
                setError(errorMsg);
                if (onError) onError(errorMsg);
            }
        } catch (error) {
            console.error('Error fetching overview data:', error);
            const errorMsg = 'Failed to fetch overview data';
            setError(errorMsg);
            if (onError) onError(errorMsg);
        } finally {
            if (onLoading) onLoading(false);
        }
    }, [onError, onLoading]); // Dependencies for useCallback


    useEffect(() => {
        fetchOverviewData();
    }, [fetchOverviewData]); // Include fetchOverviewData as dependency


    const StatCard = ({ title, value, icon, color = 'primary' }) => (
        <Card>
            <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography color="textSecondary" gutterBottom>
                            {title}
                        </Typography>
                        <Typography variant="h4" color={`${color}.main`}>
                            {value.toLocaleString()}
                        </Typography>
                    </Box>
                    <Box color={`${color}.main`}>
                        {icon}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );

    return (
        <Box>
            <Typography variant="h5" gutterBottom>
                Dashboard Overview
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Total Users"
                        value={overviewData.totalUsers}
                        icon={<PeopleIcon sx={{ fontSize: 40 }} />}
                        color="primary"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Quiz Sessions"
                        value={overviewData.totalQuizSessions}
                        icon={<QuizIcon sx={{ fontSize: 40 }} />}
                        color="secondary"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Total Score"
                        value={overviewData.totalScore}
                        icon={<TrendingUpIcon sx={{ fontSize: 40 }} />}
                        color="success"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Recent Registrations"
                        value={overviewData.recentRegistrations}
                        icon={<PersonAddIcon sx={{ fontSize: 40 }} />}
                        color="warning"
                    />
                </Grid>
            </Grid>

            <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                    Quick Stats
                </Typography>
                <Typography variant="body1" color="textSecondary">
                    This dashboard provides an overview of user activity and quiz performance.
                    Use the tabs above to dive deeper into specific analytics.
                </Typography>
            </Box>
        </Box>
    );
};

export default DashboardOverview;
